/**
 * Audit Logs Table Component
 * Display paginated audit log entries with details
 */

import React from "react";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  HStack,
  VStack,
  Text,
  useColorModeValue,
  Skeleton,
  Center,
  Spinner,
  Button,
  Icon,
  Tooltip,
} from "@chakra-ui/react";
import { FiChevronLeft, FiChevronRight, FiExternalLink } from "react-icons/fi";
import { AuditLogEntry, PaginatedAuditLogs } from "./types";
import { formatDateTimeForDisplay } from "./utils";

interface AuditLogsTableProps {
  data: PaginatedAuditLogs | null;
  loading?: boolean;
  onPageChange?: (page: number) => void;
}

interface StatusBadgeProps {
  success: boolean;
}

const StatusBadge: React.FC<StatusBadgeProps> = ({ success }) => {
  return (
    <Badge
      colorScheme={success ? "green" : "red"}
      variant="solid"
      px={2}
      py={1}
      borderRadius="md"
    >
      {success ? "Berhasil" : "Gagal"}
    </Badge>
  );
};

interface SourceBadgeProps {
  source: string;
}

const SourceBadge: React.FC<SourceBadgeProps> = ({ source }) => {
  const colorMap: Record<string, string> = {
    mobile: "blue",
    web: "green",
    api: "purple",
  };
  return (
    <Badge colorScheme={colorMap[source] || "gray"} variant="outline">
      {source.toUpperCase()}
    </Badge>
  );
};

interface MethodBadgeProps {
  method: string;
}

const MethodBadge: React.FC<MethodBadgeProps> = ({ method }) => {
  return (
    <Badge colorScheme={method === "ldap" ? "orange" : "cyan"} variant="subtle">
      {method.toUpperCase()}
    </Badge>
  );
};

const AuditLogsTable: React.FC<AuditLogsTableProps> = ({
  data,
  loading = false,
  onPageChange,
}) => {
  const bg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const hoverBg = useColorModeValue("gray.50", "gray.700");
  const headerBg = useColorModeValue("gray.50", "gray.700");
  const textColor = useColorModeValue("gray.700", "gray.200");
  const mutedColor = useColorModeValue("gray.500", "gray.400");

  if (loading) {
    return (
      <Box
        bg={bg}
        border="1px solid"
        borderColor={borderColor}
        borderRadius="lg"
        overflow="hidden"
      >
        <VStack spacing={2} p={4}>
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} height="40px" width="100%" />
          ))}
        </VStack>
      </Box>
    );
  }

  if (!data || data.data.length === 0) {
    return (
      <Box
        bg={bg}
        border="1px solid"
        borderColor={borderColor}
        borderRadius="lg"
        p={8}
      >
        <Center>
          <Text color={mutedColor}>Tidak ada data audit log yang ditemukan</Text>
        </Center>
      </Box>
    );
  }

  const handlePreviousPage = () => {
    if (data.page > 1 && onPageChange) {
      onPageChange(data.page - 1);
    }
  };

  const handleNextPage = () => {
    if (data.page < data.total_pages && onPageChange) {
      onPageChange(data.page + 1);
    }
  };

  return (
    <VStack align="stretch" spacing={4}>
      <Box
        bg={bg}
        border="1px solid"
        borderColor={borderColor}
        borderRadius="lg"
        overflow="hidden"
      >
        <Box overflowX="auto">
          <Table size="sm">
            <Thead bg={headerBg}>
              <Tr>
                <Th>Waktu</Th>
                <Th>Email</Th>
                <Th>Status</Th>
                <Th>Metode</Th>
                <Th>Sumber</Th>
                {/* <Th>Device ID</Th>
                <Th>Device Model</Th> */}
                <Th>Alasan Gagal</Th>
              </Tr>
            </Thead>
            <Tbody>
              {data.data.map((log: AuditLogEntry, index: number) => (
                <Tr
                  key={log.id}
                  _hover={{ bg: hoverBg }}
                  borderBottom="1px solid"
                  borderColor={borderColor}
                >
                  <Td fontSize="sm" color={textColor}>
                    {formatDateTimeForDisplay(log.created_at)}
                  </Td>
                  <Td>
                    <Tooltip label={log.email} placement="top">
                      <Text fontSize="sm" maxW="250px">
                        {log.email}
                      </Text>
                    </Tooltip>
                  </Td>
                  <Td>
                    <StatusBadge success={log.success} />
                  </Td>
                  <Td>
                    <MethodBadge method={log.method} />
                  </Td>
                  <Td>
                    <SourceBadge source={log.source} />
                  </Td>
                  {/* <Td>
                    <Tooltip label={log.device_id || "N/A"} placement="top">
                      <Text fontSize="sm" isTruncated maxW="120px">
                        {log.device_id ? log.device_id.substring(0, 12) + "..." : "-"}
                      </Text>
                    </Tooltip>
                  </Td>
                  <Td fontSize="sm" color={textColor}>
                    {log.device_model || "-"}
                  </Td> */}
                  <Td>
                    {log.failure_reason ? (
                      <Tooltip label={log.failure_reason} placement="top">
                        <Text fontSize="sm" isTruncated maxW="120px" color="red.500">
                          {log.failure_reason}
                        </Text>
                      </Tooltip>
                    ) : (
                      <Text fontSize="sm" color={mutedColor}>
                        -
                      </Text>
                    )}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      </Box>

      {/* Pagination */}
      <HStack justify="space-between" align="center" flexWrap="wrap" gap={4}>
        <Text fontSize="sm" color={mutedColor}>
          Menampilkan {data.data.length} dari {data.total} data • Halaman {data.page} dari{" "}
          {data.total_pages}
        </Text>

        <HStack spacing={2}>
          <Button
            size="sm"
            variant="outline"
            leftIcon={<FiChevronLeft />}
            onClick={handlePreviousPage}
            isDisabled={data.page === 1}
          >
            Sebelumnya
          </Button>

          <HStack
            border="1px solid"
            borderColor={borderColor}
            borderRadius="md"
            px={3}
            py={1}
          >
            <Text fontSize="sm" fontWeight="600">
              {data.page}
            </Text>
          </HStack>

          <Button
            size="sm"
            variant="outline"
            rightIcon={<FiChevronRight />}
            onClick={handleNextPage}
            isDisabled={data.page === data.total_pages}
          >
            Berikutnya
          </Button>
        </HStack>
      </HStack>
    </VStack>
  );
};

export default AuditLogsTable;
